import React, { PureComponent, RefObject, useEffect, useState } from "react";

import { MenuProps } from "rc-menu/lib/Menu";
import { SubMenuProps as RCSubMenuProps } from "rc-menu/lib/SubMenu";
import { MenuItemProps as RCMenuItemProps } from "rc-menu/lib/MenuItem";
import { MenuMode } from "rc-menu/lib/interface";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import "rc-menu/assets/index.css";
import { mdiDotsHorizontal, mdiChevronDown } from "@mdi/js";
import style from "./style.module.scss";
import classNames from "classnames";
import { CSSMotionProps } from "rc-motion";

import Menu, { SubMenu as RCSubMenu, MenuItem as RCMenuItem } from "rc-menu";

interface Props {
    mode?: MenuMode;
    motion?: Record<MenuProps["mode"], CSSMotionProps>;
    children?: string | JSX.Element | JSX.Element[];
    triggerSubMenuAction?: "hover" | "click";
    ariaLabel?: string;
    whiteBg?: boolean;
}

interface MenuItemProps extends RCMenuItemProps {
    index?: number;
    activePath?: boolean;
    id?: string;
}
interface SubMenuProps extends RCSubMenuProps {
    activePath?: boolean;
    id?: string;
}

const PrepareProps = (props: SubMenuProps | MenuItemProps): SubMenuProps | MenuItemProps => {
    const childProps = { ...props };
    delete childProps.activePath;
    childProps.className = props.activePath ? classNames("active-path", childProps.className) : childProps.className;

    return childProps;
};

const MenuItem = (props: MenuItemProps): JSX.Element => {
    const [childProps, setChildProps] = useState(PrepareProps(props));

    useEffect(() => {
        setChildProps(PrepareProps(props));
    }, [props]);

    return (
        <RCMenuItem
            {...childProps}
            title={null}
            data-test-index={`Navigation.MenuItem.${props.index}`}
            data-test-id={childProps?.id || `Navigation.MenuItem.${props.index}`}
        >
            <Typography>{props.children}</Typography>
        </RCMenuItem>
    );
};

const SubMenu = (props: SubMenuProps): JSX.Element => {
    const childProps = PrepareProps(props);

    return (
        <RCSubMenu
            data-test-id={`Navigation.SubMenu.${props.title}`}
            {...childProps}
            title={
                <div className="rc-menu-submenu-container" data-test-id="Navigation.SubMenu.Title">
                    <Typography>{props.title}</Typography>
                    <Icon icon={mdiChevronDown} size="small" />
                </div>
            }
            popupClassName="navigation__popup"
            popupOffset={[0, 0]}
        >
            {props.children}
        </RCSubMenu>
    );
};

export class Navigation extends PureComponent<Props> {
    static MenuItem = MenuItem;
    static SubMenu = SubMenu;

    popupContainer: RefObject<HTMLDivElement> = React.createRef();

    render() {
        const {
            mode = "horizontal",
            children,
            triggerSubMenuAction = "hover",
            ariaLabel = null,
            whiteBg = false,
        } = this.props;

        const overflowedIndicator = (
            <Typography data-test-id="Navigation.Overflow" aria-label={ariaLabel}>
                <Icon icon={mdiDotsHorizontal} size="small" />
            </Typography>
        );

        return (
            <>
                <div ref={this.popupContainer}></div>
                <Menu
                    triggerSubMenuAction={triggerSubMenuAction}
                    mode={mode}
                    className={classNames(style.navigation, whiteBg ? style.whiteBg : null, whiteBg ? "whiteBg" : null)}
                    overflowedIndicator={overflowedIndicator}
                    expandIcon={<></>}
                    getPopupContainer={() => {
                        return this.popupContainer.current;
                    }}
                    data-test-id="Navigation"
                    defaultMotions={this.props.motion}
                >
                    {children}
                </Menu>
            </>
        );
    }
}
